/**
 * SitesTab - Projects/Sites management (PROIECTE)
 * Extracted from MasterDashboard.jsx
 * Shows: Gantt chart, project list with CRUD, add project dialog
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Clock, Users, Calendar as CalendarIcon, BarChart3, FileText, DollarSign, Link2, KeyRound, Copy, Check } from "lucide-react";
import GanttChart from "@/components/dashboard/GanttChart";
import ProjectFinanceDialog from "@/components/dashboard/ProjectFinanceDialog";
import { HelpTooltip } from "@/components/HelpTooltip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SitesTab({ companyId, user, onOpenSiteSettings, onOpenReport, onOpenContracts, onOpenPhases }) {
  const [sites, setSites] = useState([]);
  const [openSiteDialog, setOpenSiteDialog] = useState(false);
  const [newSite, setNewSite] = useState({ name: "", address: "", work_start_time: "07:00", work_end_time: "16:00", start_date: "", end_date: "", total_value: "", progress_percent: 0, expenses: "" });
  const [portalLinkDialog, setPortalLinkDialog] = useState(null);
  const [generatedLink, setGeneratedLink] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [codeDialog, setCodeDialog] = useState(false);
  const [selectedSitesForCode, setSelectedSitesForCode] = useState([]);
  const [codeClientName, setCodeClientName] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);
  const [financeSite, setFinanceSite] = useState(null);

  const fetchSites = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/sites?company_id=${companyId}`);
      // Sortăm descrescător după created_at (cel mai nou primul)
      // Fallback la id (UUID v7 timestamp-based) sau name dacă created_at lipsește
      const sorted = [...(response.data || [])].sort((a, b) => {
        const ta = a?.created_at || a?.updated_at || "";
        const tb = b?.created_at || b?.updated_at || "";
        if (ta && tb) return tb.localeCompare(ta);
        // Fallback: by id descending (păstrează un ordin stabil)
        return String(b?.id || "").localeCompare(String(a?.id || ""));
      });
      setSites(sorted);
    } catch (error) { console.error("Error fetching sites:", error); }
  }, [companyId]);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  const handleAddSite = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/sites?company_id=${companyId}`, { ...newSite, total_value: parseFloat(newSite.total_value) || 0, expenses: parseFloat(newSite.expenses) || 0 });
      setNewSite({ name: "", address: "", work_start_time: "07:00", work_end_time: "16:00", start_date: "", end_date: "", total_value: "", progress_percent: 0, expenses: "" });
      setOpenSiteDialog(false);
      fetchSites();
      toast.success("Proiect adăugat cu succes!");
    } catch (error) { toast.error("Eroare la adăugarea proiectului"); }
  };

  const handleDeleteSite = async (id) => {
    if (!window.confirm("Sigur vrei să ștergi acest proiect?")) return;
    try {
      await axios.delete(`${API}/sites/${id}?company_id=${companyId}`);
      fetchSites();
      toast.success("Proiect șters!");
    } catch (error) { toast.error("Eroare la ștergere"); }
  };

  const handleGenerateLink = async (site) => {
    try {
      const res = await axios.post(`${API}/client-portal/generate-link`, {
        site_id: site.id,
        company_id: companyId,
      });
      // Preferă short_link (prietenos) dacă backend-ul îl returnează;
      // fallback la link vechi pentru compatibilitate.
      const path = res.data.short_link || res.data.link;
      const fullLink = `${window.location.origin}${path}`;
      setGeneratedLink(fullLink);
      setPortalLinkDialog(site);
      setLinkCopied(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la generarea linkului");
    }
  };

  const handleGenerateCode = async () => {
    if (selectedSitesForCode.length === 0) {
      toast.error("Selectați cel puțin un proiect");
      return;
    }
    try {
      const res = await axios.post(`${API}/client-portal/generate-login-code`, {
        company_id: companyId,
        client_name: codeClientName,
        site_ids: selectedSitesForCode,
      });
      setGeneratedCode(res.data.code);
      setCodeCopied(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la generarea codului");
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === "link") setLinkCopied(true);
      else setCodeCopied(true);
      toast.success("Copiat!");
      setTimeout(() => { setLinkCopied(false); setCodeCopied(false); }, 2000);
    } catch (error) { toast.error("Nu s-a putut copia"); }
  };

  return (
    <div className="space-y-6">
      {/* Gantt Chart */}
      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            <CalendarIcon className="w-6 h-6 text-teal-600" />Calendar Proiecte (Gantt)
            <HelpTooltip section="gantt" />
          </CardTitle>
          <CardDescription>Vizualizează timeline-ul proiectelor și urmărește progresul</CardDescription>
        </CardHeader>
        <CardContent>
          <GanttChart projects={sites} onEditProject={(project) => onOpenSiteSettings?.(project)} companyName={user?.company_name || "e-Tesa"} companyId={companyId} onRefresh={fetchSites} onOpenPhases={onOpenPhases} />
        </CardContent>
      </Card>
      
      {/* Projects List */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>PROIECTE<HelpTooltip section="santiere" /></CardTitle>
            <CardDescription>Gestionează proiectele și setările lor</CardDescription>
          </div>
          <Dialog open={openSiteDialog} onOpenChange={setOpenSiteDialog}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-site-button"><Plus className="w-4 h-4 mr-2" />Adaugă Proiect</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>Proiect Nou</DialogTitle></DialogHeader>
              <form onSubmit={handleAddSite} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="site-name">Nume Proiect</Label>
                    <Input id="site-name" value={newSite.name} onChange={(e) => setNewSite({...newSite, name: e.target.value})} placeholder="ex: Construcție Bloc A" required />
                  </div>
                  <div className="col-span-2">
                    <Label htmlFor="site-address">Adresă / Locație</Label>
                    <Input id="site-address" value={newSite.address} onChange={(e) => setNewSite({...newSite, address: e.target.value})} placeholder="ex: Str. Exemplu Nr. 10, Iași" required />
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><CalendarIcon className="w-4 h-4 text-teal-600" />Planificare Proiect</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Data Început</Label><Input type="date" value={newSite.start_date} onChange={(e) => setNewSite({...newSite, start_date: e.target.value})} /></div>
                    <div><Label>Data Sfârșit</Label><Input type="date" value={newSite.end_date} onChange={(e) => setNewSite({...newSite, end_date: e.target.value})} /></div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-600" />Aspect Financiar</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Valoare Totală Proiect (RON)</Label><Input type="number" value={newSite.total_value} onChange={(e) => setNewSite({...newSite, total_value: e.target.value})} placeholder="ex: 500000" /></div>
                    <div><Label>Cheltuieli Inițiale (RON)</Label><Input type="number" value={newSite.expenses} onChange={(e) => setNewSite({...newSite, expenses: e.target.value})} placeholder="ex: 0" /></div>
                  </div>
                </div>
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-blue-600" />Program de Lucru</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Ora Intrare</Label><Input type="time" value={newSite.work_start_time} onChange={(e) => setNewSite({...newSite, work_start_time: e.target.value})} /></div>
                    <div><Label>Ora Ieșire</Label><Input type="time" value={newSite.work_end_time} onChange={(e) => setNewSite({...newSite, work_end_time: e.target.value})} /></div>
                  </div>
                </div>
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-2" />Adaugă Proiect</Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {sites.map((site) => (
              <div key={site.id} className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl border border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex-1">
                  <p className="font-semibold text-lg cursor-pointer hover:text-teal-600 transition-colors" onClick={() => onOpenPhases?.(site)} data-testid={`open-phases-${site.id}`}>{site.name}</p>
                  <p className="text-sm text-slate-600">{site.address}</p>
                  <div className="flex flex-wrap gap-3 mt-2">
                    <span className="text-xs text-slate-500"><Clock className="w-3 h-3 inline mr-1" />{site.work_start_time} - {site.work_end_time}</span>
                    {site.start_date && <span className="text-xs text-teal-600"><CalendarIcon className="w-3 h-3 inline mr-1" />{site.start_date} → {site.end_date || 'N/A'}</span>}
                    {site.total_value > 0 && <span className="text-xs text-green-600 font-medium">💰 {new Intl.NumberFormat('ro-RO', { style: 'currency', currency: 'RON' }).format(site.total_value)}</span>}
                    {site.progress_percent > 0 && <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Progres: {site.progress_percent}%</Badge>}
                  </div>
                  {site.assigned_team?.length > 0 && <p className="text-xs text-teal-600 mt-2"><Users className="w-3 h-3 inline mr-1" />Echipă: {site.assigned_team.length} angajați</p>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleGenerateLink(site)} className="text-teal-600 border-teal-300 hover:bg-teal-50" data-testid={`generate-link-${site.id}`}><Link2 className="w-4 h-4 mr-1" />Link Client</Button>
                  <Button variant="outline" size="sm" onClick={() => setFinanceSite(site)} className="text-green-600 border-green-300 hover:bg-green-50" data-testid={`finance-${site.id}`}><DollarSign className="w-4 h-4 mr-1" />Financiar</Button>
                  <Button variant="outline" size="sm" onClick={() => onOpenContracts?.(site)} className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"><FileText className="w-4 h-4 mr-1" />Contracte</Button>
                  <Button variant="outline" size="sm" onClick={() => onOpenReport?.(site)} className="text-blue-600 border-blue-300 hover:bg-blue-50"><BarChart3 className="w-4 h-4 mr-1" />Raport</Button>
                  <Button variant="outline" size="sm" onClick={() => onOpenSiteSettings?.(site)}>Setări</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteSite(site.id)} className="text-red-500 hover:text-red-700 hover:bg-red-50"><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Generate Login Code button */}
      <Card className="border-0 shadow-lg">
        <CardContent className="p-5 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Cod de logare client
            </h3>
            <p className="text-sm text-slate-500">Generați un cod pentru clienții cu mai multe proiecte</p>
          </div>
          <Button
            onClick={() => { setCodeDialog(true); setGeneratedCode(""); setSelectedSitesForCode([]); setCodeClientName(""); }}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="generate-code-btn"
          >
            <KeyRound className="w-4 h-4 mr-2" />Generează Cod
          </Button>
        </CardContent>
      </Card>

      {/* Portal Link Dialog */}
      <Dialog open={!!portalLinkDialog} onOpenChange={() => setPortalLinkDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-teal-600" />
              Link Portal Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Link generat pentru: <b>{portalLinkDialog?.name}</b>
            </p>
            <div className="flex gap-2">
              <Input value={generatedLink} readOnly className="text-xs bg-slate-50" data-testid="generated-link-input" />
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(generatedLink, "link")}
                data-testid="copy-link-btn"
              >
                {linkCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Trimiteți acest link clientului. Va avea acces doar la proiectul selectat.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Login Code Dialog */}
      <Dialog open={codeDialog} onOpenChange={setCodeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-amber-600" />
              Generare Cod Logare Client
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nume client</Label>
              <Input
                value={codeClientName}
                onChange={(e) => setCodeClientName(e.target.value)}
                placeholder="Nume client"
                data-testid="code-client-name"
              />
            </div>
            <div>
              <Label className="mb-2 block">Selectați proiecte</Label>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {sites.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedSitesForCode.includes(s.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSelectedSitesForCode([...selectedSitesForCode, s.id]);
                        else setSelectedSitesForCode(selectedSitesForCode.filter(id => id !== s.id));
                      }}
                      className="rounded"
                      data-testid={`code-site-${s.id}`}
                    />
                    <span className="text-sm">{s.name}</span>
                  </label>
                ))}
              </div>
            </div>
            {!generatedCode ? (
              <Button onClick={handleGenerateCode} className="w-full bg-amber-500 hover:bg-amber-600" data-testid="generate-code-submit">
                <KeyRound className="w-4 h-4 mr-2" />Generează Cod
              </Button>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-slate-600">Cod generat:</p>
                <div className="flex gap-2">
                  <Input value={generatedCode} readOnly className="text-lg font-mono font-bold text-center bg-amber-50 tracking-widest" data-testid="generated-code-input" />
                  <Button size="sm" variant="outline" onClick={() => copyToClipboard(generatedCode, "code")} data-testid="copy-code-btn">
                    {codeCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-slate-500">
                  Clientul va folosi acest cod pentru a-și crea contul și accesa proiectele selectate.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ProjectFinanceDialog
        open={!!financeSite}
        onOpenChange={(o) => { if (!o) setFinanceSite(null); }}
        companyId={companyId}
        site={financeSite}
      />
    </div>
  );
}
